'use client';

import { type ArrayNode, getNode, setNode } from '@inixiative/rules-builder';
import { VocabularySelect } from '@zealot/ui/components/vocabulary-select';
import { useContext, useMemo } from 'react';
import {
  BuilderContext,
  commitDeferred,
  MutateContext,
  type RuleBuilderValue,
  type SelectorSpec,
  sectionedItems,
  selectorClause,
  selectorIsVisible,
  selectorItems,
  selectorMultiCount,
} from './lib';

// gloss
export const SelectorControl = ({
  node,
  selector,
  onPick,
}: {
  node: ArrayNode;
  selector: SelectorSpec;
  onPick?: () => void;
}) => {
  const { sourceValues, source, decoration } = useContext(BuilderContext);
  const { value, setCondition } = useContext(MutateContext);
  const clause = selectorClause(node, selector);
  const multiCount = selectorMultiCount(node, selector);

  const items = useMemo(
    () => selectorItems(sourceValues, source, node, selector, decoration),
    [sourceValues, source, node, selector, decoration],
  );
  const anyValue = '~any';
  const multiValue = '~multi';
  const menu = useMemo(
    () => [
      { value: anyValue, label: selector.anyLabel ?? 'Any' },
      ...(multiCount ? [{ value: multiValue, label: `${multiCount} selected` }] : []),
      ...sectionedItems(items.length ? items : (clause?.value?.options ?? [])).map((option) => ({
        ...option,
        value: `~v:${String(option.value)}`,
      })),
    ],
    [items, clause, selector.anyLabel, multiCount],
  );
  if (!selectorIsVisible(node, selector, sourceValues, source, decoration) && !multiCount) return null;

  const pick = (next: string | null) => {
    if (node.setSelectorClause) {
      node.setSelectorClause(selector.field, next);
      return;
    }
    if (clause) {
      if (next == null) clause.remove();
      else clause.value?.set(next);
      return;
    }
    if (next == null || !value || !setCondition) return;
    const target = getNode(value, node.path) as Record<string, unknown> | undefined;
    if (!target || target.field !== node.field.value) return;
    const inner = (target.condition ?? { all: [] }) as Record<string, unknown>;
    const operatorKey: 'all' | 'any' = Array.isArray(inner.any) ? 'any' : 'all';
    const conditions = Array.isArray(inner[operatorKey]) ? (inner[operatorKey] as unknown[]) : [inner];
    const appended = {
      ...target,
      condition: {
        ...(Array.isArray(inner[operatorKey]) ? inner : {}),
        [operatorKey]: [...conditions, { field: selector.field, operator: 'equals', value: next }],
      },
    };
    setCondition(setNode(value, node.path, appended as never) as RuleBuilderValue);
  };

  return (
    <VocabularySelect
      aria-label={selector.label ?? 'Field'}
      items={menu}
      value={multiCount ? multiValue : clause?.value?.current == null ? anyValue : `~v:${String(clause.value.current)}`}
      onValueChange={(next) => {
        if (next === multiValue) return;
        const picked = next === anyValue || next == null ? null : next.slice('~v:'.length);
        commitDeferred(() => pick(picked));
        if (picked != null) onPick?.();
      }}
      placeholder={selector.anyLabel ?? 'Any'}
      className="w-44"
    />
  );
};
